import React, { useState, useEffect, useRef } from "react";
import Navigation from "../Navigation";
import ConnectionStatus from "../Dashboard/ConnectionStatus";
import {
  FaComments,
  FaLanguage,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaCalendarDay,
  FaCalendarWeek,
} from "react-icons/fa";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import {
  fetchChatLogAnalytics,
  fetchTranslateLogAnalytics,
  fetchQueryLogAnalytics,
} from "../../services/service";
import { useAsgardeoAuth } from "../../hooks/useAsgardeoAuth";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

// Reusable StatCard component
const StatCard = ({ value, label, color, iconBg, icon, subtitle }) => (
  <div
    className={`bg-white rounded-xl shadow-lg p-4 border-l-4 ${color} transition-all duration-200 hover:shadow-xl`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div
        className={`w-12 h-12 flex items-center justify-center rounded-full ${iconBg}`}
      >
        {icon}
      </div>
    </div>
  </div>
);

// Section Header component
const SectionHeader = ({ children, icon }) => (
  <div className="flex items-center mb-6">
    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
      {icon}
    </div>
    <h2 className="text-xl font-bold text-gray-900">{children}</h2>
  </div>
);

// Helper to compute today/week counts from perDay array
const computeCounts = (perDay = []) => {
  const today = new Date().toISOString().split("T")[0];
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - 6);

  let todayCount = 0;
  let todayFailed = 0;
  let todaySuccess = 0;
  let weekCount = 0;

  perDay.forEach((day) => {
    const dayDate = new Date(day.date);
    // Handle both old and new API response formats
    const total = day.totalCount || day.total || 0;
    const success = day.successCount || day.success || day.highConfidence || 0;
    const failed = day.failCount || day.failed ||day.lowConfidence || 0;

    if (day.date === today) {
      todayCount = total;
      todaySuccess = success;
      todayFailed = failed;
    }
    if (dayDate >= weekStart) {
      weekCount += total;
    }
  });

  return { todayCount, todaySuccess, todayFailed, weekCount };
};

const MasterDashboard = () => {
  const { getAccessToken, getIDToken, getDecodedIDToken } = useAsgardeoAuth();
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const minFetchInterval = 2000; // Minimum 2 seconds between fetches
  const [dashboardData, setDashboardData] = useState({
    chat: {
      todayCount: 0,
      weekCount: 0,
      failedCount: 0,
      totalCount: 0,
      successCount: 0,
    },
    translate: {
      todayCount: 0,
      weekCount: 0,
      failedCount: 0,
      totalCount: 0,
      successCount: 0,
    },
    query: {
      todayCount: 0,
      weekCount: 0,
      failedCount: 0,
      totalCount: 0,
      successCount: 0,
    },
  });
  const [analyticsData, setAnalyticsData] = useState({
    chat: { perDay: [], perHour: [] },
    translate: { perDay: [], perHour: [] },
    query: { perDay: [], perHour: [] },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchAllDashboardData = async (force = false) => {
    const now = Date.now();
    
    // Prevent multiple simultaneous fetches and too frequent fetches
    if (isFetchingRef.current) {
      console.log('Fetch already in progress, skipping...');
      return;
    }
    
    if (!force && (now - lastFetchTimeRef.current) < minFetchInterval) {
      console.log('Fetch too recent, skipping...');
      return;
    }

    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request batch
    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;
    
    setLoading(true);
    try {
      const authContext = { getAccessToken, getIDToken, getDecodedIDToken };

      console.log('Starting API calls with auth context:', {
        hasGetAccessToken: !!authContext.getAccessToken,
        hasGetIDToken: !!authContext.getIDToken,
        hasGetDecodedIDToken: !!authContext.getDecodedIDToken
      });

      const [
        chatAnalytics,
        translateAnalytics,
        queryAnalytics,
      ] = await Promise.all([
        fetchChatLogAnalytics(authContext),
        fetchTranslateLogAnalytics(authContext),
        fetchQueryLogAnalytics(authContext),
      ]);

      // Debug logging to see the actual API response structure
      console.log('Chat Analytics Response:', chatAnalytics);
      console.log('Translate Analytics Response:', translateAnalytics);
      console.log('Query Analytics Response:', queryAnalytics);

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      // Calculate counts dynamically
      const chatCounts = computeCounts(chatAnalytics.perDay || []);
      const translateCounts = computeCounts(translateAnalytics.perDay || []);
      const queryCounts = computeCounts(queryAnalytics.perDay || []);

      // Debug logging for perDay data structure
      console.log('Chat perDay data:', chatAnalytics.perDay);
      console.log('Translate perDay data:', translateAnalytics.perDay);
      console.log('Query perDay data:', queryAnalytics.perDay);
      console.log('Computed counts:', { chatCounts, translateCounts, queryCounts });

      // Helper function to get total counts from analytics data
      const getTotalCount = (analytics, type) => {
        // Handle the new API response structure
        if (type === 'Chats') {
          return analytics.totalChats || analytics.total || 0;
        } else if (type === 'Translates') {
          return analytics.totalTranslates || analytics.total || 0;
        } else if (type === 'Queries') {
          return analytics.totalQueries || analytics.total || 0;
        }
        return analytics.total || 0;
      };

      const getTotalSuccess = (analytics) => {
        return analytics.totalSuccess || analytics.success ||analytics.totalHighConfidence|| 0;
      };

      const getTotalFailed = (analytics) => {
        return analytics.totalFailed || analytics.failed ||analytics.totalLowConfidence|| 0;
      };

      setDashboardData({
        chat: {
          todayCount: chatCounts.todayCount,
          weekCount: chatCounts.weekCount,
          failedCount: getTotalFailed(chatAnalytics),
          totalCount: getTotalCount(chatAnalytics, 'Chats'),
          successCount: getTotalSuccess(chatAnalytics),
        },
        translate: {
          todayCount: translateCounts.todayCount,
          weekCount: translateCounts.weekCount,
          failedCount: getTotalFailed(translateAnalytics),
          totalCount: getTotalCount(translateAnalytics, 'Translates'),
          successCount: getTotalSuccess(translateAnalytics),
        },
        query: {
          todayCount: queryCounts.todayCount,
          weekCount: queryCounts.weekCount,
          failedCount: getTotalFailed(queryAnalytics),
          totalCount: getTotalCount(queryAnalytics, 'Queries'),
          successCount: getTotalSuccess(queryAnalytics),
        },
      });

      setAnalyticsData({
        chat: {
          perDay: chatAnalytics.perDay || [],
          perHour: chatAnalytics.failedByHour || Array(24).fill(0),
        },
        translate: {
          perDay: translateAnalytics.perDay || [],
          perHour: translateAnalytics.failedByHour || Array(24).fill(0),
        },
        query: {
          perDay: queryAnalytics.perDay || [],
          perHour: queryAnalytics.failedByHour || Array(24).fill(0),
        },
      });

      setLastUpdate(new Date());
      setError("");
    } catch (err) {
      // Only log and set error if component is still mounted and it's not an abort error
      if (isMountedRef.current) {
        if (err.name === 'AbortError') {
          console.log('Dashboard data fetch was cancelled');
          // Don't set error for aborted requests
        } else {
          console.error("Failed to load dashboard data:", err);
          console.error("Error details:", {
            name: err.name,
            message: err.message,
            stack: err.stack,
            timestamp: new Date().toISOString()
          });
          setError(`Failed to load dashboard data: ${err.message}. Please try again.`);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    isFetchingRef.current = false;
    lastFetchTimeRef.current = 0;
    
    // Use a small delay to prevent immediate fetch on mount
    const initialFetchTimer = setTimeout(() => {
      if (isMountedRef.current) {
        fetchAllDashboardData(true); // Force initial fetch
      }
    }, 100);
    
    const interval = setInterval(() => {
      if (isMountedRef.current && !isFetchingRef.current) {
        fetchAllDashboardData();
      }
    }, 5 * 60 * 1000);
    
    return () => {
      isMountedRef.current = false;
      isFetchingRef.current = false;
      clearTimeout(initialFetchTimer);
      clearInterval(interval);
      // Abort any ongoing requests when component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [getAccessToken, getIDToken, getDecodedIDToken]);

  // Calculate totals
  const totalToday =
    dashboardData.chat.todayCount +
    dashboardData.translate.todayCount +
    dashboardData.query.todayCount;
  const totalWeek =
    dashboardData.chat.weekCount +
    dashboardData.translate.weekCount +
    dashboardData.query.weekCount;
  const totalFailed =
    dashboardData.chat.failedCount +
    dashboardData.translate.failedCount +
    dashboardData.query.failedCount;
  const totalLogs =
    dashboardData.chat.totalCount +
    dashboardData.translate.totalCount +
    dashboardData.query.totalCount;

  // Chart data
  const overallBarChartData = {
    labels: ["Chat Logs", "Translation Logs", "Query Logs"],
    datasets: [
      {
        label: "Today",
        data: [
          dashboardData.chat.todayCount,
          dashboardData.translate.todayCount,
          dashboardData.query.todayCount,
        ],
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 2,
      },
      {
        label: "This Week",
        data: [
          dashboardData.chat.weekCount,
          dashboardData.translate.weekCount,
          dashboardData.query.weekCount,
        ],
        backgroundColor: "rgba(16, 185, 129, 0.8)",
        borderColor: "rgba(16, 185, 129, 1)",
        borderWidth: 2,
      },
    ],
  };

  const pieChartData = {
    labels: ["Chat Logs", "Translation Logs", "Query Logs"],
    datasets: [
      {
        data: [
          dashboardData.chat.totalCount,
          dashboardData.translate.totalCount,
          dashboardData.query.totalCount,
        ],
        backgroundColor: [
          "rgba(59, 130, 246, 0.8)",
          "rgba(16, 185, 129, 0.8)",
          "rgba(245, 158, 11, 0.8)",
        ],
        borderColor: [
          "rgba(59, 130, 246, 1)",
          "rgba(16, 185, 129, 1)",
          "rgba(245, 158, 11, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <><Navigation />
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Master Dashboard
          </h1>
          <p className="text-gray-600 mb-2">
            Comprehensive overview of all system logs and analytics
            {lastUpdate && (
              <span className="ml-2 text-sm text-gray-500">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        {/* Overall Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            value={totalToday}
            label="Total Today"
            color="border-blue-500"
            iconBg="bg-blue-100"
            icon={<FaCalendarDay className="text-blue-600 text-xl" />}
            subtitle="All log types combined"
          />
          <StatCard
            value={totalWeek}
            label="Total This Week"
            color="border-green-500"
            iconBg="bg-green-100"
            icon={<FaCalendarWeek className="text-green-600 text-xl" />}
            subtitle="7-day period"
          />
          <StatCard
            value={totalFailed}
            label="Total Failed"
            color="border-red-500"
            iconBg="bg-red-100"
            icon={<FaTimesCircle className="text-red-600 text-xl" />}
            subtitle="Failed logs today"
          />
          <StatCard
            value={totalLogs}
            label="Total Logs"
            color="border-purple-500"
            iconBg="bg-purple-100"
            icon={<FaComments className="text-purple-600 text-xl" />}
            subtitle="All-time total"
          />
        </div>
        {/* Individual Log Type Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Chat Logs */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <SectionHeader icon={<FaComments className="text-blue-600" />}>
              Chat Logs
            </SectionHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboardData.chat.todayCount}
                  </p>
                  <p className="text-sm text-gray-600">Today</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {dashboardData.chat.weekCount}
                  </p>
                  <p className="text-sm text-gray-600">This Week</p>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Failed: {dashboardData.chat.failedCount}
                </span>
                <span className="text-gray-600">
                  Success: {dashboardData.chat.successCount}
                </span>
              </div>

            </div>
          </div>

          {/* Translation Logs */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <SectionHeader icon={<FaLanguage className="text-green-600" />}>
              Translation Logs
            </SectionHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {dashboardData.translate.todayCount}
                  </p>
                  <p className="text-sm text-gray-600">Today</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {dashboardData.translate.weekCount}
                  </p>
                  <p className="text-sm text-gray-600">This Week</p>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Failed: {dashboardData.translate.failedCount}
                </span>
                <span className="text-gray-600">
                  Success: {dashboardData.translate.successCount}
                </span>
              </div>

            </div>
          </div>

          {/* Query Logs */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <SectionHeader icon={<FaSearch className="text-orange-600" />}>
              Query Logs
            </SectionHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {dashboardData.query.todayCount}
                  </p>
                  <p className="text-sm text-gray-600">Today</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {dashboardData.query.weekCount}
                  </p>
                  <p className="text-sm text-gray-600">This Week</p>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Failed: {dashboardData.query.failedCount}
                </span>
                <span className="text-gray-600">
                  Success: {dashboardData.query.successCount}
                </span>
              </div>

            </div>
          </div>
        </div>
        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Overall Bar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Log Activity Comparison
            </h3>
            <div className="h-80">
              <Bar data={overallBarChartData} options={chartOptions} />
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Total Logs Distribution
            </h3>
            <div className="h-80">
              <Pie data={pieChartData} options={chartOptions} />
            </div>
          </div>
        </div>
        {/* Failed Logs by Hour */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Failed Logs by Hour (Today)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Chat Logs
              </h4>
              <div className="h-32">
                <Bar
                  data={{
                    labels: Array.from({ length: 24 }, (_, i) => i),
                    datasets: [
                      {
                        label: "Failed",
                        data: analyticsData.chat.perHour,
                        backgroundColor: "rgba(59, 130, 246, 0.8)",
                        borderColor: "rgba(59, 130, 246, 1)",
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    ...chartOptions,
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Translation Logs
              </h4>
              <div className="h-32">
                <Bar
                  data={{
                    labels: Array.from({ length: 24 }, (_, i) => i),
                    datasets: [
                      {
                        label: "Failed",
                        data: analyticsData.translate.perHour,
                        backgroundColor: "rgba(16, 185, 129, 0.8)",
                        borderColor: "rgba(16, 185, 129, 1)",
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    ...chartOptions,
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Query Logs
              </h4>{" "}
              <div className="h-32">
                {" "}
                <Bar
                  data={{
                    labels: Array.from({ length: 24 }, (_, i) => i),
                    datasets: [
                      {
                        label: "Failed",
                        data: analyticsData.query.perHour,
                        backgroundColor: "rgba(245, 158, 11, 0.8)",
                        borderColor: "rgba(245, 158, 11, 1)",
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    ...chartOptions,
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />{" "}
              </div>{" "}
            </div>{" "}
          </div>{" "}
        </div>{" "}
        {/* Refresh Button */}
        <div className="text-center">
          <button
            onClick={() => fetchAllDashboardData(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center mx-auto"
          >
            <FaExclamationTriangle className="mr-2" /> Refresh Dashboard
          </button>
        </div>
      </div>
    </div>
</>
  );
};
export default MasterDashboard;
